// audit-safety-integrated
import { readJson, safeErrorMessage } from '../utils/auditSafety';
import { useState, useEffect, useMemo, useRef } from "react";
import { API_BASE_URL } from '../services/api';
import { FileText, ChevronDown, ChevronRight } from "lucide-react";
import { DateRange } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { enUS } from "date-fns/locale";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const periodOptions = [
  { value: "thisweek",  label: "This Week" },
  { value: "lastweek",  label: "Last Week" },
  { value: "thismonth", label: "This Month" },
  { value: "lastmonth", label: "Last Month" },
];

const groupByOptions = [
  { value: "store",       label: "Store" },
  { value: "branch_type", label: "Branch Type" },
  { value: "department",  label: "Department" },
  { value: "branch",      label: "Branch" },
];

const formatHeader = (key) =>
  key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());

// Mini SVG sparkline line graph
// Green = last rate <= first rate (stable/down), Red = last rate > first rate (up/increase)
const PriceTrend = ({ rates }) => {
  if (!rates || rates.length === 0) return <span className="text-gray-300 text-xs">—</span>;

  const validRates = rates.filter(v => v !== null && v !== undefined && !isNaN(Number(v))).map(Number);
  if (validRates.length === 0) return <span className="text-gray-300 text-xs">—</span>;

  const W = 100, H = 28, PAD = 3;
  const minV = Math.min(...validRates);
  const maxV = Math.max(...validRates);
  const spread = maxV === minV ? 0 : ((maxV - minV) / minV) * 100;
  const isUp = validRates[validRates.length - 1] > validRates[0];
  const color = isUp ? '#ef4444' : '#22c55e';

  const toX = (i) => PAD + (i / Math.max(validRates.length - 1, 1)) * (W - PAD * 2);
  const toY = (v) => maxV === minV
    ? H / 2
    : PAD + ((maxV - v) / (maxV - minV)) * (H - PAD * 2);

  const points = validRates.map((v, i) => `${toX(i)},${toY(v)}`).join(' ');

  return (
    <div className="flex flex-col items-center gap-0.5">
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`}>
        <polyline
          points={points}
          fill="none"
          stroke={color}
          strokeWidth="1.8"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        {/* dot at last point */}
        <circle
          cx={toX(validRates.length - 1)}
          cy={toY(validRates[validRates.length - 1])}
          r="2.5"
          fill={color}
        />
      </svg>
      <span className={`text-[10px] font-semibold ${isUp ? 'text-red-500' : 'text-green-600'}`}>
        {isUp ? '▲' : '▼'} {spread.toFixed(1)}%
      </span>
    </div>
  );
};

const isISODate = (val) =>
  typeof val === "string" && /^\d{4}-\d{2}-\d{2}T/.test(val);

const formatDate = (val) => {
  if (!val) return val;
  const d = new Date(val);
  if (isNaN(d.getTime())) return val;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

// Detect numeric-looking fields for aggregation
const isNumeric = (v) => v !== null && v !== undefined && v !== "" && !isNaN(Number(v));

export default function PrisonReport() {
  const [data, setData]               = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [search, setSearch]           = useState("");
  const [period, setPeriod]           = useState("thisweek");
  const [groupBy]                      = useState("store");
  const [departments, setDepartments] = useState([]);
  const [stores, setStores]           = useState([]);
  const [deptFilter, setDeptFilter]   = useState("");
  const [storeFilter, setStoreFilter] = useState("");
  // expanded: Map<item_name, groupByKey> — which items are open and what they're grouped by
  const [expanded, setExpanded]       = useState(new Map());
  // expandedDept: Set of "itemName::deptVal" keys — which dept rows are expanded to show branch types
  const [expandedDept, setExpandedDept] = useState(new Set());
  // expandedStore: Set of "itemName::storeVal" keys — which store rows are expanded to show indent details
  const [expandedStore, setExpandedStore] = useState(new Set());
  const [activeCategory, setActiveCategory] = useState('');
  const [showCalendar, setShowCalendar] = useState(false);
  const [customLabel, setCustomLabel]   = useState('');
  const [dateRange, setDateRange]       = useState([{ startDate: new Date(), endDate: new Date(), key: 'selection' }]);
  const calendarRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (calendarRef.current && !calendarRef.current.contains(e.target)) setShowCalendar(false);
    };
    if (showCalendar) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showCalendar]);

  useEffect(() => {
    const init = async () => {
      try {

        const [deptRes, storeRes] = await Promise.all([
          fetch(`${API_BASE_URL}/user/departments`, { method: 'GET', headers: {  'Content-Type': 'application/json' } }),
          fetch(`${API_BASE_URL}/master/stores`,    { method: 'GET', headers: {  'Content-Type': 'application/json' } }),
        ]);
        const deptResult  = await readJson(deptRes);
        const storeResult = await readJson(storeRes);
        const deptList    = (!deptResult.error && deptResult.data) ? deptResult.data : [];
        const storeList   = (!storeResult.error && storeResult.data)
          ? Object.entries(storeResult.data).map(([id, name]) => ({ id, name }))
          : [];
        setDepartments(deptList);
        setStores(storeList);
        await fetchData(period, deptFilter, deptList);
      } catch (err) {
        void 0;
        await fetchData(period, deptFilter, []);
      }
    };
    init();
  }, []);

  const transformRows = (rows, deptList) => {
    return rows.map(row => {
      const t = { ...row };
      // Replace department id with name
      const deptKey = Object.keys(t).find(k => k === 'department_id' || k === 'department');
      if (deptKey) {
        const match = deptList.find(d => String(d.id) === String(t[deptKey]));
        if (match) t[deptKey] = match.name;
      }
      // Remove id fields
      delete t['id'];
      // Format ISO dates
      Object.keys(t).forEach(k => {
        if (isISODate(t[k])) t[k] = formatDate(t[k]);
      });
      return t;
    });
  };

  const formatApiDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const fetchData = async (selectedPeriod = period, selectedDept = deptFilter, deptList = departments) => {
    setLoading(true);
    setError(null);
    setExpanded(new Map());
    setExpandedDept(new Set());
    setExpandedStore(new Set());
    setActiveCategory('');
    try {

      let periodParam = selectedPeriod;
      if (selectedPeriod === 'custom') {
        const start = formatApiDate(dateRange[0].startDate);
        const end   = formatApiDate(dateRange[0].endDate);
        periodParam = `${start}:${end}`;
      }
      const params = new URLSearchParams({ period: periodParam });
      if (selectedDept) params.set('department_id', selectedDept);
      const res = await fetch(`${API_BASE_URL}/report/indent-items?${params.toString()}`, {
        method: 'GET',
        headers: {  'Content-Type': 'application/json' },
      });
      const json = await readJson(res);
      if (!res.ok || json.error) throw new Error(json.message || `HTTP ${res.status}`);
      const raw = json.data || json || [];
      // Sort by date field before transforming so _rows order is chronological
      const dateKey = raw.length > 0 ? Object.keys(raw[0]).find(k => isISODate(raw[0][k])) : null;
      if (dateKey) raw.sort((a, b) => new Date(a[dateKey]) - new Date(b[dateKey]));
      setData(transformRows(raw, deptList));
    } catch (err) {
      setError(safeErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handlePeriodChange = (val) => {
    setPeriod(val);
    if (val === 'custom') { setShowCalendar(true); return; }
    setShowCalendar(false);
    setCustomLabel('');
    fetchData(val, deptFilter);
  };
  const handleDeptChange   = (val) => { setDeptFilter(val); fetchData(period, val); };

  const toggleItem = (itemName, grpKey) => {
    setExpanded(prev => {
      const next = new Map(prev);
      if (next.has(itemName) && next.get(itemName) === grpKey) {
        next.delete(itemName); // collapse if same link clicked again
      } else {
        next.set(itemName, grpKey);
      }
      return next;
    });
  };
  const expandAll   = () => setExpanded(new Map(itemRows.map(r => [r.item_name, groupBy])));
  const collapseAll = () => setExpanded(new Map());

  // Field classifiers
  const isRateField     = (k) => /rate|price/i.test(k);
  const isQtyField      = (k) => /qty|quantity/i.test(k);
  const isValueField    = (k) => /value|amount|total/i.test(k);
  const isCategoryField = (k) => /category|cat/i.test(k);
  const isUnitField     = (k) => /^unit$|^uom$|unit_of|^unit_name$/i.test(k);

  // Detect the item name key
  const itemKey = useMemo(() => {
    if (data.length === 0) return null;
    const keys = Object.keys(data[0]);
    return keys.find(k => k === 'item_name') ||
           keys.find(k => k === 'name') ||
           keys.find(k => k.includes('item')) ||
           keys[0];
  }, [data]);

  const categoryKey = useMemo(() => {
    if (data.length === 0) return null;
    return Object.keys(data[0]).find(isCategoryField) || null;
  }, [data]);

  const unitKey = useMemo(() => {
    if (data.length === 0) return null;
    return Object.keys(data[0]).find(isUnitField) || null;
  }, [data]);

  // Detect numeric fields for aggregation (qty, price, amount, etc.)
  const numericFields = useMemo(() => {
    if (data.length === 0) return [];
    return Object.keys(data[0]).filter(k =>
      k !== itemKey && !groupByOptions.map(g => g.value).includes(k) &&
      data.slice(0, 20).some(r => isNumeric(r[k]))
    );
  }, [data, itemKey]);

  // Non-numeric, non-groupby, non-item detail columns shown in drill-down rows
  const drillColumns = useMemo(() => {
    if (data.length === 0) return [];
    const groupKeys = groupByOptions.map(g => g.value);
    return Object.keys(data[0]).filter(k =>
      k !== itemKey &&
      k !== categoryKey &&
      k !== unitKey &&
      k !== 'indent_id' &&
      k !== 'item_id' &&
      !groupKeys.includes(k) &&
      !isValueField(k)
    );
  }, [data, itemKey, categoryKey, unitKey]);


  // Filtered flat rows
  const filteredRows = useMemo(() => {
    let rows = data;
    if (storeFilter) rows = rows.filter(r => r['store'] === storeFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter(r => Object.values(r).some(v => String(v).toLowerCase().includes(q)));
    }
    return rows;
  }, [data, search, storeFilter]);

  // Aggregate flat rows → one row per item
  const itemRows = useMemo(() => {
    if (!itemKey) return [];
    const map = {};
    filteredRows.forEach(row => {
      const name = row[itemKey] ?? "—";
      if (!map[name]) {
        map[name] = {
          item_name: name,
          _category: row[categoryKey] ?? '',
          _unit:     row[unitKey]     ?? '',
          _item_id:  row['item_id']   ?? null,
          _rows: [], _totals: {}, _min: {}, _max: {}
        };
        numericFields.forEach(f => {
          map[name]._totals[f] = 0;
          map[name]._min[f]    = Infinity;
          map[name]._max[f]    = -Infinity;
        });
      }
      map[name]._rows.push(row);
      numericFields.forEach(f => {
        const v = Number(row[f]);
        if (!isNaN(v)) {
          map[name]._totals[f] += v;
          if (v < map[name]._min[f]) map[name]._min[f] = v;
          if (v > map[name]._max[f]) map[name]._max[f] = v;
        }
      });
    });
    return Object.values(map).sort((a, b) =>
      Number(a._item_id) - Number(b._item_id)
    );
  }, [filteredRows, itemKey, numericFields]);

  // Label mapping for category tabs
  const categoryLabel = (raw) => {
    const r = (raw || '').toLowerCase();
    if (r.includes('food')) return 'Grocery';
    if (r.includes('daili') || r.includes('daily') || r.includes('dairy') || r.includes('veg') || r.includes('meat')) return 'Veg/Meat Dairy';
    if (r.includes('house')) return 'House Keeping';
    return raw; // fallback: show raw value for any other categories
  };

  // Unique categories sorted: food-like first, then rest
  const categories = useMemo(() => {
    const cats = [...new Set(itemRows.map(r => r._category).filter(Boolean))];
    // put food first
    cats.sort((a, b) => {
      const aIsFood = /food/i.test(a);
      const bIsFood = /food/i.test(b);
      if (aIsFood && !bIsFood) return -1;
      if (!aIsFood && bIsFood) return 1;
      return a.localeCompare(b);
    });
    return cats;
  }, [itemRows]);

  // Auto-select first tab when categories load
  useEffect(() => {
    if (categories.length > 0 && !activeCategory) {
      setActiveCategory(categories[0]);
    }
  }, [categories]);

  // Items filtered by active category tab
  const visibleItems = useMemo(() => {
    if (!activeCategory) return itemRows;
    return itemRows.filter(r => r._category === activeCategory);
  }, [itemRows, activeCategory]);

  const toggleDept = (itemName, deptVal) => {
    const key = `${itemName}::${deptVal}`;
    setExpandedDept(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const toggleStore = (itemName, storeVal) => {
    const key = `${itemName}::${storeVal}`;
    setExpandedStore(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const aggregateRows = (rows) => {
    const agg = { _totals: {}, _min: {}, _max: {} };
    numericFields.forEach(f => { agg._totals[f] = 0; agg._min[f] = Infinity; agg._max[f] = -Infinity; });
    rows.forEach(row => {
      numericFields.forEach(f => {
        const v = Number(row[f]);
        if (!isNaN(v)) {
          agg._totals[f] += v;
          if (v < agg._min[f]) agg._min[f] = v;
          if (v > agg._max[f]) agg._max[f] = v;
        }
      });
    });
    return agg;
  };

  // Drill-down: group rows by a given key
  const getDrillGroups = (rows, grpKey) => {
    const map = {};
    rows.forEach(row => {
      const key = row[grpKey] ?? "—";
      if (!map[key]) map[key] = [];
      map[key].push(row);
    });
    return Object.entries(map).sort(([a], [b]) => String(a).localeCompare(String(b)));
  };

  // Unique count helpers
  const uniqueCount = (rows, key) => new Set(rows.map(r => r[key]).filter(Boolean)).size;

  const fmtRate = (v) => {
    if (v === Infinity || v === -Infinity) return "—";
    const n = Number(v);
    return isNaN(n) ? "—" : n.toFixed(2);
  };
  const fmtQty = (v) => {
    if (v === Infinity || v === -Infinity) return "—";
    const n = Number(v);
    return isNaN(n) ? "—" : n.toFixed(3);
  };

  const summaryColumns = useMemo(() => {
    const rate = numericFields.filter(isRateField);
    const qty  = numericFields.filter(isQtyField);
    return [...rate, ...qty].map(f => ({
      key:  f,
      label: formatHeader(f),
      kind: isRateField(f) ? 'rate' : isQtyField(f) ? 'qty' : 'value',
    }));
  }, [numericFields]);

  const handleExport = async () => {
    if (itemRows.length === 0) return;
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Orders Price Comparison");

    const now = new Date();
    const today = now.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    const dateStr = now.toISOString().slice(0, 10);
    const periodLabel = periodOptions.find(p => p.value === period)?.label || customLabel || period;

    // Build header columns: #, Category, Item, then rate cols (Min/Max), then qty cols (Total)
    const rateFields = summaryColumns.filter(c => c.kind === 'rate');
    const qtyFields  = summaryColumns.filter(c => c.kind === 'qty');

    // Count total data columns to compute merge range
    // cols: # | Category | Item | [Rate Min, Rate Max] * n | [Qty Total] * m
    const totalDataCols = 2 + rateFields.length * 2 + qtyFields.length; // category + item + rate pairs + qty
    const totalCols = 1 + totalDataCols; // +1 for #
    const colLetter = (n) => n <= 26 ? String.fromCharCode(64 + n) : String.fromCharCode(64 + Math.floor((n - 1) / 26)) + String.fromCharCode(64 + ((n - 1) % 26) + 1);
    const lastColLetter = colLetter(totalCols);

    // Title
    ws.mergeCells(`A1:${lastColLetter}1`);
    ws.getCell("A1").value = "Orders Price Comparison";
    ws.getCell("A1").font = { bold: true, size: 14 };
    ws.getCell("A1").alignment = { horizontal: "center" };

    ws.mergeCells(`A2:${lastColLetter}2`);
    ws.getCell("A2").value = `Period: ${periodLabel}   |   Generated: ${today}`;
    ws.getCell("A2").font = { size: 10, color: { argb: "FF666666" } };
    ws.getCell("A2").alignment = { horizontal: "center" };

    ws.addRow([]);

    // Sub-header row 1: group headers
    // # | Category | Item | Rate label (span 2) ... | Qty label (span 1) ...
    let col = 1;
    const headerRow1 = ws.getRow(4);
    const setHCell = (c, val, span, bgArgb) => {
      const cell = headerRow1.getCell(c);
      cell.value = val;
      cell.font = { bold: true, size: 10 };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bgArgb || "FFD6E4F7" } };
      cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
      if (span > 1) ws.mergeCells(4, c, 4, c + span - 1);
    };
    setHCell(1, "#", 1, "FFE2E8F0");
    setHCell(2, "Category", 1, "FFD6E4F7");
    setHCell(3, "Item", 1, "FFD6E4F7");
    col = 4;
    rateFields.forEach(c => { setHCell(col, c.label, 2, "FFFDE68A"); col += 2; });
    qtyFields.forEach(c  => { setHCell(col, c.label, 1, "FFD1FAE5"); col += 1; });

    // Sub-header row 2: Min / Max / Total labels
    const headerRow2 = ws.getRow(5);
    const setH2Cell = (c, val, bgArgb) => {
      const cell = headerRow2.getCell(c);
      cell.value = val;
      cell.font = { bold: true, size: 9 };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bgArgb || "FFF0F4FF" } };
      cell.alignment = { horizontal: "center", vertical: "middle" };
    };
    setH2Cell(1, "#", "FFE2E8F0");
    setH2Cell(2, "Category", "FFF0F4FF");
    setH2Cell(3, "Item", "FFF0F4FF");
    col = 4;
    rateFields.forEach(() => { setH2Cell(col, "Min", "FFFEF9C3"); setH2Cell(col + 1, "Max", "FFFEF9C3"); col += 2; });
    qtyFields.forEach(()  => { setH2Cell(col, "Total", "FFECFDF5"); col += 1; });

    // Group itemRows by category, sort: food first
    const catOrder = [...new Set(itemRows.map(r => r._category))].sort((a, b) => {
      const aF = /food/i.test(a), bF = /food/i.test(b);
      if (aF && !bF) return -1; if (!aF && bF) return 1; return a.localeCompare(b);
    });

    let rowNum = 6;
    let globalIdx = 1;
    catOrder.forEach(cat => {
      const catItems = itemRows.filter(r => r._category === cat);
      if (catItems.length === 0) return;
      const catLabel = categoryLabel(cat);

      // Category header row
      const catRow = ws.getRow(rowNum++);
      ws.mergeCells(rowNum - 1, 1, rowNum - 1, totalCols);
      const catCell = catRow.getCell(1);
      catCell.value = catLabel;
      catCell.font = { bold: true, size: 11, color: { argb: "FF1E40AF" } };
      catCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE0EAFF" } };
      catCell.alignment = { horizontal: "left", vertical: "middle", indent: 1 };

      // Item rows under this category
      catItems.forEach(item => {
        const dataRow = ws.getRow(rowNum++);
        dataRow.getCell(1).value = globalIdx++;
        dataRow.getCell(1).alignment = { horizontal: "center", vertical: "middle" };

        dataRow.getCell(2).value = catLabel;
        dataRow.getCell(2).alignment = { horizontal: "left", vertical: "middle" };

        dataRow.getCell(3).value = item.item_name + (item._unit ? ` (${item._unit})` : '');
        dataRow.getCell(3).alignment = { horizontal: "left", vertical: "middle" };

        col = 4;
        rateFields.forEach(c => {
          dataRow.getCell(col).value     = item._min[c.key] === Infinity  ? null : parseFloat(item._min[c.key].toFixed(2));
          dataRow.getCell(col).numFmt    = '0.00';
          dataRow.getCell(col).alignment = { horizontal: "right", vertical: "middle" };
          dataRow.getCell(col).font      = { color: { argb: "FF15803D" } };
          dataRow.getCell(col + 1).value  = item._max[c.key] === -Infinity ? null : parseFloat(item._max[c.key].toFixed(2));
          dataRow.getCell(col + 1).numFmt = '0.00';
          dataRow.getCell(col + 1).alignment = { horizontal: "right", vertical: "middle" };
          dataRow.getCell(col + 1).font  = { color: { argb: "FFC2410C" } };
          col += 2;
        });
        qtyFields.forEach(c => {
          dataRow.getCell(col).value     = parseFloat(item._totals[c.key].toFixed(3));
          dataRow.getCell(col).numFmt    = '0.000';
          dataRow.getCell(col).alignment = { horizontal: "right", vertical: "middle" };
          col += 1;
        });

        // Zebra shading
        const bg = globalIdx % 2 === 0 ? "FFFAFAFA" : "FFFFFFFF";
        for (let c2 = 1; c2 <= totalCols; c2++) {
          const cell = dataRow.getCell(c2);
          if (!cell.fill || cell.fill.fgColor?.argb === "FFFFFFFF" || !cell.fill.fgColor) {
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bg } };
          }
        }
      });
    });

    ws.columns = [
      { width: 5 },   // #
      { width: 18 },  // Category
      { width: 30 },  // Item
      ...rateFields.flatMap(() => [{ width: 12 }, { width: 12 }]),
      ...qtyFields.map(() => ({ width: 12 })),
    ];

    ws.getRow(4).height = 30;
    ws.getRow(5).height = 20;

    const buffer = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), `Orders_Price_Comparison_${dateStr}.xlsx`);
  };

  return (
    <div className="max-w-7xl mx-auto p-2 bg-gray-50 rounded-lg">
      <div className="bg-white shadow rounded-lg p-6 h-full flex flex-col">

        {/* Page header */}
        <div className="flex items-center justify-between mb-5">
          <h1 className="text-xl font-semibold text-gray-800">Orders Price Comparison</h1>
          <div className="flex items-center gap-2">
            <button
              onClick={handleExport}
              disabled={itemRows.length === 0}
              className="flex items-center gap-2 bg-green-600 text-white px-3 py-2 rounded hover:bg-green-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FileText className="w-4 h-4" />
              Export
            </button>
            <button
              onClick={() => fetchData()}
              className="flex items-center gap-2 border border-gray-300 text-gray-600 px-3 py-2 rounded hover:bg-gray-100 text-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-end gap-4 mb-5">
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Period</span>
            <div className="relative inline-block">
              <select
                value={period}
                onClick={e => { if (e.target.value === 'custom') setShowCalendar(true); }}
                onChange={e => handlePeriodChange(e.target.value)}
                className="border border-gray-300 rounded px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white cursor-pointer"
              >
                <option value="thisweek">This Week</option>
                <option value="lastweek">Last Week</option>
                <option value="thismonth">This Month</option>
                <option value="lastmonth">Last Month</option>
                <option value="custom">{customLabel || 'Custom'}</option>
              </select>

              {showCalendar && (
                <div ref={calendarRef} className="absolute left-0 mt-2 bg-white shadow-lg border border-gray-200 rounded-lg p-3 z-50">
                  <DateRange
                    editableDateInputs={true}
                    onChange={item => setDateRange([item.selection])}
                    moveRangeOnFirstSelection={false}
                    ranges={dateRange}
                    rangeColors={['#2563eb']}
                    months={1}
                    direction="horizontal"
                    locale={enUS}
                  />
                  <div className="flex justify-end gap-2 mt-3">
                    <button
                      onClick={() => {
                        setShowCalendar(false);
                        const start = dateRange[0].startDate;
                        const end   = dateRange[0].endDate;
                        const fmt = (d) => `${d.toLocaleDateString('en-US', { month: 'short' })} ${d.getDate()}`;
                        setCustomLabel(`${fmt(start)} - ${fmt(end)}`);
                        setPeriod('custom');
                        fetchData('custom', deptFilter);
                      }}
                      className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                    >
                      Select
                    </button>
                    <button
                      onClick={() => { setShowCalendar(false); setPeriod('thisweek'); setCustomLabel(''); }}
                      className="border border-gray-300 px-3 py-1 rounded text-sm hover:bg-gray-100"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

<div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Department</span>
            <select value={deptFilter} onChange={e => handleDeptChange(e.target.value)}
              className="border border-gray-300 rounded px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white">
              <option value="">All Departments</option>
              {departments.map(dept => (
                <option key={dept.id} value={dept.id}>{dept.name}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Store</span>
            <select value={storeFilter} onChange={e => setStoreFilter(e.target.value)}
              className="border border-gray-300 rounded px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white">
              <option value="">All Stores</option>
              {stores.map(s => (
                <option key={s.id} value={s.name}>{s.name}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1 ml-auto">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Search</span>
            <div className="relative w-48">
              <input type="text" placeholder="Search items..." value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-9 pr-4 py-1.5 w-full rounded border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm" />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M15 11a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Category Tabs */}
        {!loading && !error && categories.length > 0 && (
          <div className="flex gap-1 border-b border-gray-200 mb-4 overflow-x-auto">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => { setActiveCategory(cat); setExpanded(new Map()); setExpandedDept(new Set()); setExpandedStore(new Set()); }}
                className={`px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeCategory === cat
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {categoryLabel(cat)}
              </button>
            ))}
          </div>
        )}

        {/* Table */}
        {loading ? (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
            <p className="mt-2 text-gray-600 text-sm">Loading...</p>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-600 font-medium">Failed to load data</p>
            <p className="text-gray-500 text-sm mt-1">{error}</p>
          </div>
        ) : visibleItems.length === 0 ? (
          <div className="text-center py-12 text-gray-500 text-sm">No data found.</div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-2">
              <div className="flex gap-2">
                <button onClick={expandAll} className="text-xs text-blue-600 hover:underline">Expand All</button>
                <span className="text-gray-300 text-xs">|</span>
                <button onClick={collapseAll} className="text-xs text-blue-600 hover:underline">Collapse All</button>
              </div>
              <span className="text-xs text-gray-400">
                {visibleItems.length} items · {filteredRows.length} indent records
              </span>
            </div>

            <div className="flex-grow overflow-auto">
              <table className="w-full table-auto border-collapse border border-gray-300 text-sm">
                {/* Summary header */}
                <thead className="sticky top-0 z-10">
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 px-3 py-2 w-8 text-center text-gray-500">#</th>
                    <th className="border border-gray-300 px-4 py-2 text-left font-medium text-gray-700">Description</th>
                    {summaryColumns.map(c => (
                      <th key={c.key} colSpan={c.kind === 'rate' ? 2 : 1}
                        className="border border-gray-300 px-4 py-2 text-center font-medium text-gray-700">
                        {c.label}
                      </th>
                    ))}
                    <th className="border border-gray-300 px-4 py-2 text-center font-medium text-gray-700 w-[15%]">Price Trend</th>
                  </tr>
                  {summaryColumns.length > 0 && (
                    <tr className="bg-gray-50 text-xs text-gray-500">
                      <th className="border border-gray-300 px-2 py-1" />
                      <th className="border border-gray-300 px-2 py-1" />
                      {summaryColumns.map(c => (
                        c.kind === 'rate' ? (
                          <>
                            <th key={`${c.key}-min`} className="border border-gray-300 px-3 py-1 text-center font-medium">Min</th>
                            <th key={`${c.key}-max`} className="border border-gray-300 px-3 py-1 text-center font-medium">Max</th>
                          </>
                        ) : (
                          <th key={`${c.key}-total`} className="border border-gray-300 px-3 py-1 text-center font-medium">Total</th>
                        )
                      ))}
                      <th className="border border-gray-300 px-2 py-1 text-center font-medium">Spread</th>
                    </tr>
                  )}
                </thead>

                <tbody>
                  {visibleItems.map((item, idx) => {
                    const activeGrp    = expanded.get(item.item_name);
                    const isOpen       = expanded.has(item.item_name);
                    const drillGroups  = isOpen ? getDrillGroups(item._rows, activeGrp) : [];
                    const deptCount    = uniqueCount(item._rows, 'department');
                    const storeCount   = uniqueCount(item._rows, 'store');

                    return (
                      <>
                        {/* Item summary row */}
                        <tr key={`item-${item.item_name}`} className="hover:bg-indigo-50 bg-white">
                          <td className="border border-gray-200 px-3 py-2 text-center text-gray-400 text-xs">{idx + 1}</td>
                          <td className="border border-gray-200 px-4 py-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-gray-800">{item.item_name}</span>
                              {item._unit && (
                                <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded font-normal">{item._unit}</span>
                              )}
                              <div className="flex items-center gap-1 ml-1">
                                {deptCount > 0 && (
                                  <button
                                    onClick={() => toggleItem(item.item_name, 'department')}
                                    className={`text-xs px-2 py-0.5 rounded-full border transition-colors ${
                                      activeGrp === 'department'
                                        ? 'bg-amber-500 text-white border-amber-500'
                                        : 'text-amber-600 border-amber-300 hover:bg-amber-50'
                                    }`}
                                  >
                                    {deptCount} Dept{deptCount > 1 ? 's' : ''}
                                  </button>
                                )}
                                {storeCount > 0 && (
                                  <button
                                    onClick={() => toggleItem(item.item_name, 'store')}
                                    className={`text-xs px-2 py-0.5 rounded-full border transition-colors ${
                                      activeGrp === 'store'
                                        ? 'bg-blue-500 text-white border-blue-500'
                                        : 'text-blue-600 border-blue-300 hover:bg-blue-50'
                                    }`}
                                  >
                                    {storeCount} Store{storeCount > 1 ? 's' : ''}
                                  </button>
                                )}
                              </div>
                            </div>
                          </td>
                          {summaryColumns.map(c => (
                            c.kind === 'rate' ? (
                              <>
                                <td key={`${item.item_name}-${c.key}-n`} className="border border-gray-200 px-3 py-2 text-right text-green-700">
                                  {fmtRate(item._min[c.key])}
                                </td>
                                <td key={`${item.item_name}-${c.key}-x`} className="border border-gray-200 px-3 py-2 text-right text-orange-600">
                                  {fmtRate(item._max[c.key])}
                                </td>
                              </>
                            ) : (
                              <td key={`${item.item_name}-${c.key}-t`} className="border border-gray-200 px-3 py-2 text-right font-semibold text-gray-700">
                                {fmtQty(item._totals[c.key])}
                              </td>
                            )
                          ))}
                          <td className="border border-gray-200 px-3 py-2 text-center">
                            {(() => {
                              const rateField = summaryColumns.find(c => c.kind === 'rate');
                              if (!rateField) return <span className="text-gray-300 text-xs">—</span>;
                              const rates = item._rows.map(r => r[rateField.key]).filter(v => v !== null && v !== undefined && !isNaN(Number(v)));
                              return <PriceTrend rates={rates} />;
                            })()}
                          </td>
                        </tr>

                        {/* Drill-down: one aggregated row per sub-group */}
                        {isOpen && drillGroups.map(([grpVal, grpRows], gi) => {
                          const agg = aggregateRows(grpRows);
                          const isDeptDrill  = activeGrp === 'department';
                          const isStoreDrill = activeGrp === 'store';

                          const deptKey  = `${item.item_name}::${grpVal}`;
                          const isDeptOpen  = isDeptDrill  && expandedDept.has(deptKey);
                          const isStoreOpen = isStoreDrill && expandedStore.has(deptKey);

                          const branchGroups = isDeptOpen  ? getDrillGroups(grpRows, 'branch_type') : [];
                          const totalCols    = summaryColumns.reduce((a, c) => a + (c.kind === 'rate' ? 2 : 1), 0);

                          return (
                            <>
                              {/* Sub-group aggregated row */}
                              <tr key={`grp-${item.item_name}-${grpVal}`}
                                onClick={() => {
                                  if (isDeptDrill)  toggleDept(item.item_name, grpVal);
                                  if (isStoreDrill) toggleStore(item.item_name, grpVal);
                                }}
                                className={`text-xs ${gi % 2 === 0 ? "bg-blue-50" : "bg-indigo-50"} cursor-pointer hover:bg-blue-100`}
                              >
                                <td className="border border-gray-200 px-3 py-2 text-center text-gray-400">{gi + 1}</td>
                                <td className="border border-gray-200 px-4 py-2 text-gray-700 font-medium">
                                  <div className="flex items-center gap-2 pl-6">
                                    <span className="text-blue-500">
                                      {(isDeptOpen || isStoreOpen) ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                                    </span>
                                    {grpVal}
                                  </div>
                                </td>
                                {summaryColumns.map(c => (
                                  c.kind === 'rate' ? (
                                    <>
                                      <td key={`${grpVal}-${c.key}-n`} className="border border-gray-200 px-3 py-2 text-right text-green-700">{fmtRate(agg._min[c.key])}</td>
                                      <td key={`${grpVal}-${c.key}-x`} className="border border-gray-200 px-3 py-2 text-right text-orange-600">{fmtRate(agg._max[c.key])}</td>
                                    </>
                                  ) : (
                                    <td key={`${grpVal}-${c.key}-t`} className="border border-gray-200 px-3 py-2 text-right font-semibold text-gray-700">{fmtQty(agg._totals[c.key])}</td>
                                  )
                                ))}
                              </tr>

                              {/* Branch type sub-level — under department drill */}
                              {isDeptOpen && branchGroups.map(([btVal, btRows], bi) => {
                                const btAgg = aggregateRows(btRows);
                                return (
                                  <tr key={`bt-${item.item_name}-${grpVal}-${btVal}`}
                                    className={`text-xs ${bi % 2 === 0 ? "bg-gray-50" : "bg-white"} hover:bg-indigo-50`}>
                                    <td className="border border-gray-100" />
                                    <td className="border border-gray-200 px-4 py-1.5 text-gray-600 pl-14">{btVal}</td>
                                    {summaryColumns.map(c => (
                                      c.kind === 'rate' ? (
                                        <>
                                          <td key={`${btVal}-${c.key}-n`} className="border border-gray-200 px-3 py-1.5 text-right text-green-700">{fmtRate(btAgg._min[c.key])}</td>
                                          <td key={`${btVal}-${c.key}-x`} className="border border-gray-200 px-3 py-1.5 text-right text-orange-600">{fmtRate(btAgg._max[c.key])}</td>
                                        </>
                                      ) : (
                                        <td key={`${btVal}-${c.key}-t`} className="border border-gray-200 px-3 py-1.5 text-right font-semibold text-gray-600">{fmtQty(btAgg._totals[c.key])}</td>
                                      )
                                    ))}
                                  </tr>
                                );
                              })}

                              {/* Indent detail rows — under store drill */}
                              {isStoreOpen && (
                                <tr key={`sd-wrap-${item.item_name}-${grpVal}`}>
                                  <td colSpan={2 + totalCols} className="border border-gray-200 p-0 pl-10">
                                    <table className="w-full table-auto border-collapse text-xs">
                                      <thead>
                                        <tr className="bg-gray-100">
                                          <th className="border border-gray-200 px-3 py-1 text-center text-gray-500 w-8">#</th>
                                          <th className="border border-gray-200 px-4 py-1 text-left font-medium text-gray-600 whitespace-nowrap">Indent No</th>
                                          {drillColumns.map(col => (
                                            <th key={col} className="border border-gray-200 px-4 py-1 text-left font-medium text-gray-600 whitespace-nowrap">
                                              {formatHeader(col)}
                                            </th>
                                          ))}
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {grpRows.map((row, ri) => (
                                          <tr key={ri} className={ri % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                                            <td className="border border-gray-200 px-3 py-1.5 text-center text-gray-400">{ri + 1}</td>
                                            <td className="border border-gray-200 px-4 py-1.5 whitespace-nowrap text-gray-700 font-medium">{row['indent_id'] ?? "—"}</td>
                                            {drillColumns.map(col => (
                                              <td key={col} className="border border-gray-200 px-4 py-1.5 whitespace-nowrap text-gray-700">
                                                {row[col] ?? "—"}
                                              </td>
                                            ))}
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </td>
                                </tr>
                              )}
                            </>
                          );
                        })}
                      </>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

      </div>
    </div>
  );
}
